'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import {
	GEO_COUNTRY_COOKIE_NAME,
	getBrowserCookieValue,
	getMarketByCountry,
	getMarketKey,
	type StorefrontMarket,
} from '@/lib/markets';

// Only a small share of visitors ever get a suggestion — load the dialog (and
// Radix Dialog with it) on demand instead of in the store layout's bundle.
const RegionSuggestionDialog = dynamic(
	() => import('./region-suggestion-dialog').then((module) => module.RegionSuggestionDialog),
	{ ssr: false },
);

interface RegionSuggestionPopupProps {
	market: StorefrontMarket;
	supportedMarkets: StorefrontMarket[];
}

const DISMISS_STORAGE_PREFIX = 'storefront-region-suggestion-dismissed';

export function RegionSuggestionPopup({ market, supportedMarkets }: RegionSuggestionPopupProps) {
	const [suggestedMarket, setSuggestedMarket] = useState<StorefrontMarket | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	// Mount the dialog once it has opened and keep it mounted, so closing still
	// plays its exit animation.
	const [hasOpened, setHasOpened] = useState(false);

	useEffect(() => {
		const detectedCountry = getBrowserCookieValue(GEO_COUNTRY_COOKIE_NAME);
		const nextSuggestedMarket = getSuggestedMarket(market, supportedMarkets, detectedCountry);
		setSuggestedMarket(nextSuggestedMarket);

		if (!nextSuggestedMarket || hasDismissedSuggestion(market, nextSuggestedMarket)) {
			setIsOpen(false);
			return;
		}

		setIsOpen(true);
		setHasOpened(true);
	}, [market, supportedMarkets]);

	// No suggestion, or one dismissed earlier: never fetch the dialog chunk.
	if (!suggestedMarket || !hasOpened) return null;

	const dismiss = () => {
		storeDismissedSuggestion(market, suggestedMarket);
		setIsOpen(false);
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			dismiss();
			return;
		}

		setIsOpen(true);
	};

	return (
		<RegionSuggestionDialog
			market={market}
			suggestedMarket={suggestedMarket}
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			onDismiss={dismiss}
		/>
	);
}

function getSuggestedMarket(
	currentMarket: StorefrontMarket,
	supportedMarkets: StorefrontMarket[],
	detectedCountry: string | undefined,
) {
	const detectedMarket = getMarketByCountry(detectedCountry, supportedMarkets);
	if (!detectedMarket) return null;
	if (getMarketKey(detectedMarket) === getMarketKey(currentMarket)) return null;
	if (detectedMarket.currencyCode.toUpperCase() === currentMarket.currencyCode.toUpperCase()) {
		return null;
	}

	return detectedMarket;
}

function getDismissalStorageKey(
	currentMarket: StorefrontMarket,
	suggestedMarket: StorefrontMarket,
) {
	return `${DISMISS_STORAGE_PREFIX}:${getMarketKey(currentMarket)}:${getMarketKey(suggestedMarket)}`;
}

function hasDismissedSuggestion(
	currentMarket: StorefrontMarket,
	suggestedMarket: StorefrontMarket,
) {
	try {
		return (
			window.localStorage.getItem(getDismissalStorageKey(currentMarket, suggestedMarket)) === '1'
		);
	} catch {
		return false;
	}
}

function storeDismissedSuggestion(
	currentMarket: StorefrontMarket,
	suggestedMarket: StorefrontMarket,
) {
	try {
		window.localStorage.setItem(getDismissalStorageKey(currentMarket, suggestedMarket), '1');
	} catch {
		// Ignore storage failures so private browsing still allows the modal to close.
	}
}
